import { SearchAdapter } from '@empathy/search-adapter';
import { Banner, Promoted, Result, Tagging } from '@empathy/search-types';
import { StorageService } from '@empathy/storage-service';
import { deepMerge } from '@empathybroker/deep-merge';
import { container } from '../../../container';
import { DEPENDENCIES, STORAGE } from '../../../container/dependencies.const';
import { ClientEventCallbacks, EmpathyXConfig } from '../../../models';
import { ClientEventsDispatcher } from '../../../services';
import { Dictionary, isArrayEmptyOrNull } from '../../../utils';
import { GETTERS_KEYS } from '../../store.const';
import { EVENTS_CONTEXT, TRACKING_ACTIONS_KEYS } from '../tracking.const';
import { TrackingAction, TrackingContext } from '../tracking.interfaces';

export class TaggingAction implements TrackingAction {

  private adapter: SearchAdapter;
  private clientEventsDispatcher: ClientEventsDispatcher;
  private localStorageService: StorageService;
  private sessionStorageService: StorageService;

  actions = {
    [TRACKING_ACTIONS_KEYS.trackQuery]: ({ rootGetters }: TrackingContext, tagging: Tagging) => {
      this.initDependencies();
      this.adapter.track(tagging);
      const resultsInfo = rootGetters[GETTERS_KEYS.search.resultsCallbackInfo];
      const callbackInfo = !isArrayEmptyOrNull(resultsInfo) ? { results: resultsInfo } : null;
      const data = this.getCallbackData(tagging, callbackInfo);
      this.clientEventsDispatcher.dispatch({ eventName: ClientEventCallbacks.query, data });
    },
    [TRACKING_ACTIONS_KEYS.trackClick]: (context: TrackingContext, item: Result | Promoted | Banner) => {
      this.initDependencies();
      const clickTagging = item.tagging?.click;
      if (clickTagging) {
        this.adapter.track(clickTagging);
        const data = this.getCallbackData(clickTagging, item.callbackInfo);
        this.clientEventsDispatcher.dispatch({ eventName: ClientEventCallbacks.click, data });
      }
    },
    [TRACKING_ACTIONS_KEYS.trackAddToCartOnProductPage]: ({ rootGetters }: TrackingContext) => {
      this.initDependencies();
      const { tracking }: EmpathyXConfig = rootGetters[GETTERS_KEYS.config.config];
      const result: Result = this.sessionStorageService.getItem<Result>(tracking.addToCart.storage.result);
      const add2cartTagging = result?.tagging?.add2cart;
      if (add2cartTagging) {
        this.adapter.track(add2cartTagging);
        const data = this.getCallbackData(add2cartTagging, result.callbackInfo);
        this.clientEventsDispatcher.dispatch(
          { eventName: ClientEventCallbacks.add2cart, data: { ...data, context: EVENTS_CONTEXT.PDP } });
      }
    },
    [TRACKING_ACTIONS_KEYS.trackAddToCartOnResult]: (context: TrackingContext, result: Result) => {
      this.initDependencies();
      const add2cartTagging = result.tagging?.add2cart;
      if (add2cartTagging) {
        this.adapter.track(add2cartTagging);
        const data = this.getCallbackData(add2cartTagging, result.callbackInfo);
        this.clientEventsDispatcher.dispatch(
          { eventName: ClientEventCallbacks.add2cart, data: { ...data, context: EVENTS_CONTEXT.SRP } });
      }
    },
    [TRACKING_ACTIONS_KEYS.trackCheckout]: ({ rootGetters }: TrackingContext, productsIds: string | string[]) => {
      this.initDependencies();
      const { tracking }: EmpathyXConfig = rootGetters[GETTERS_KEYS.config.config];
      const ids = Array.isArray(productsIds) ? productsIds : [productsIds];
      const rawCheckout = this.localStorageService.getItem(tracking.checkout.storage.key);
      const checkout = this.getCheckoutFiltered(rawCheckout ?? {}, ids);

      if (checkout.length) {
        this.trackAndDispatchCheckout(checkout);
      }
    },
    [TRACKING_ACTIONS_KEYS.trackShow]: ({ rootGetters }: TrackingContext, tagging: Tagging) => {
      this.initDependencies();
      this.adapter.track(tagging);
    },
    [TRACKING_ACTIONS_KEYS.trackToggleWishlist]: (context: TrackingContext, item: Result) => {
      this.initDependencies();
      const wishlistTagging = item.tagging?.wishlist;
      if (wishlistTagging) {
        this.adapter.track(wishlistTagging);
      }
    }
  };

  private initDependencies() {
    this.adapter = this.adapter
      ?? container.get<SearchAdapter>(DEPENDENCIES.Adapter);
    this.clientEventsDispatcher = this.clientEventsDispatcher
      ?? container.get<ClientEventsDispatcher>(DEPENDENCIES.ClientEventsDispatcher);
    this.localStorageService = this.localStorageService
      ?? container.getNamed<StorageService>(DEPENDENCIES.StorageService, STORAGE.Local);
    this.sessionStorageService = this.sessionStorageService
      ?? container.getNamed<StorageService>(DEPENDENCIES.StorageService, STORAGE.Session);
  }

  private getCallbackData(tagging: Tagging, callbackInfo?: any): any {
    return deepMerge({}, tagging.params, callbackInfo);
  }

  private getCheckoutFiltered(checkout: Dictionary<Result>, productsIds: string[]): Result[] {
    return Object.keys(checkout)
      .filter(key => productsIds.includes(key))
      .reduce((arr, key) => {
        arr.push(checkout[key]);
        return arr;
      }, []);
  }

  private trackAndDispatchCheckout(checkout: Result[]) {
    const data: Dictionary<any> = {};

    for (const result of checkout) {
      const checkoutTagging = result.tagging?.checkout;

      if (checkoutTagging) {
        data[result.id] = this.getCallbackData(checkoutTagging, result);
        this.adapter.track(checkoutTagging);
      }
    }

    this.clientEventsDispatcher.dispatch({ eventName: ClientEventCallbacks.checkout, data });
  }
}
